/**
 * Sends a completed Stripe session to crm-service for forwarding to the GTM server container, once
 * marketing consent is known. Separate from `trackSessionPurchaseEvents` because that one is
 * synchronous and ungated, while this waits on a consent answer.
 */
import { sendMarketingEvent } from '@/lib/crm-service'

import type { GetCheckoutSessionDetailsResponse } from '@/clients/payment-service'

import { whenMarketingConsentKnown } from '../consent'
import { buildSgtmPurchaseEvent } from './sgtmPurchaseEvent'
import { hasTrackedPurchase, markPurchaseTracked, unmarkPurchaseTracked } from './trackedPurchaseSessions'

export const forwardMarketingPurchase = async (
  session: GetCheckoutSessionDetailsResponse,
  countryCode?: string,
): Promise<void> => {
  if (hasTrackedPurchase(session.session_id, 'marketing')) return

  const hasConsent = await whenMarketingConsentKnown()
  if (!hasConsent) return

  // Built after consent resolves so cookies set by consent-gated tags (_fbp/_fbc) are captured. The
  // page does not navigate while consent is pending, so the purchase-page URL context is unchanged.
  const event = buildSgtmPurchaseEvent(session, countryCode)
  if (!event) return

  // Re-checked: two sessions on one page resolve their consent promises together.
  if (hasTrackedPurchase(session.session_id, 'marketing')) return
  markPurchaseTracked(session.session_id, 'marketing')

  const sent = await sendMarketingEvent({ eventName: event.event_name, dedupeKey: session.session_id, event })
  // Release the marker when crm-service was unreachable so a later page or reload can retry;
  // crm-service owns cross-request dedupe, so a retry that does land is deduped there.
  if (!sent) unmarkPurchaseTracked(session.session_id, 'marketing')
}
